import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Copy, DollarSign, TrendingUp, Radio } from "lucide-react";
import { toast } from "sonner";

/*  /streamer/dashboard?code=XXXX — PII-safe sales + payout dashboard.
    Does NOT return buyer emails. Shows pack-level sales counts, gross,
    and the streamer's running 15% share. Bookmarkable, no login.
*/
const StreamerDashboard = () => {
  const [sp] = useSearchParams();
  const code  = (sp.get("code") || "").toUpperCase();
  const [data, setData] = useState(null);
  const [err, setErr]   = useState("");

  useEffect(() => {
    if (!code) { setErr("Missing streamer code."); return; }
    api.get(`/streamer/dashboard/${code}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load dashboard."));
  }, [code]);

  const copy = (txt) => {
    try { navigator.clipboard.writeText(txt); toast.success("Copied."); } catch {}
  };

  if (err) return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] flex items-center justify-center px-6">
      <div className="text-center">
        <div className="text-sm text-[#8b0000] mb-2">⚠</div>
        <div className="text-sm text-[#f4f0ec]">{err}</div>
        <Link to="/streamer" className="text-[#d4af37] text-xs mt-4 inline-block">← Back to the program</Link>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#d4af37]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] px-4 sm:px-6 py-10" data-testid="streamer-dashboard">
      <div className="max-w-3xl mx-auto">
        <Link to="/streamer" className="text-[10px] tracking-[0.3em] text-[#5a5050] hover:text-[#d4af37]">← program</Link>

        <div className="mt-6 mb-10">
          <div className="text-[10px] tracking-[0.4em] text-[#8b0000] mb-2">STREAMER DASHBOARD</div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#f4f0ec] leading-tight">
            @{data.handle}
          </h1>
          <div className="text-xs text-[#a39797] mt-1">
            {data.platform?.toUpperCase()} · code <span className="text-[#d4af37] tracking-[0.1em]">{data.code}</span> · {data.share_pct}% share
          </div>
          <Link
            to={`/streamer/live?code=${data.code}`}
            className="btn-primary mt-5 inline-flex"
            data-testid="dashboard-go-live"
          >
            <Radio size={14} /> Go live now
          </Link>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to={`/streamer/playbook?code=${data.code}`}
              className="btn-ghost text-xs" data-testid="dashboard-cta-playbook">
              Playbook
            </Link>
            <Link to={`/streamer/outreach?code=${data.code}`}
              className="btn-ghost text-xs" data-testid="dashboard-cta-outreach">
              Outreach kit · Comp a crew pack
            </Link>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="card-noir p-5" data-testid="dashboard-stat-sales">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] text-[#a39797] mb-2">
              <TrendingUp size={13} /> SALES
            </div>
            <div className="font-display text-4xl text-[#f4f0ec]">{data.sales}</div>
            <div className="text-[10px] text-[#5a5050] mt-1">All-time attributed</div>
          </div>
          <div className="card-noir p-5" data-testid="dashboard-stat-gross">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] text-[#a39797] mb-2">
              <DollarSign size={13} /> GROSS
            </div>
            <div className="font-display text-4xl text-[#f4f0ec]">{data.gross_label}</div>
            <div className="text-[10px] text-[#5a5050] mt-1">Through your link</div>
          </div>
          <div className="card-noir p-5 border-[#d4af37]/40" data-testid="dashboard-stat-earnings">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] text-[#d4af37] mb-2">
              <DollarSign size={13} /> YOUR CUT
            </div>
            <div className="font-display text-4xl text-[#d4af37]">{data.earnings_label}</div>
            <div className="text-[10px] text-[#5a5050] mt-1">Paid monthly</div>
          </div>
        </div>

        {/* Per-pack breakdown */}
        {data.per_pack.length > 0 ? (
          <div className="card-noir p-5 mb-8" data-testid="dashboard-per-pack">
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">BY PACK</div>
            <div className="space-y-2">
              {data.per_pack.map((p) => (
                <div key={p.pack_id} className="flex items-center justify-between py-2 border-b border-[#2a2525] last:border-0">
                  <span className="text-sm text-[#f4f0ec]">{p.pack_id.replace(/_/g, " ")}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-[#a39797]">{p.sales} sold</span>
                    <span className="text-sm text-[#d4af37]">${p.total_cents / 100}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="card-noir p-5 mb-8 text-center" data-testid="dashboard-empty">
            <div className="text-sm text-[#a39797] mb-3">No sales yet. Drop your link.</div>
          </div>
        )}

        {/* Share toolkit */}
        <div className="card-noir p-5" data-testid="dashboard-toolkit">
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">YOUR LINK</div>
          <div className="flex items-center gap-2 mb-4">
            <code className="flex-1 bg-[#1a1515] border border-[#2a2525] px-3 py-2.5 rounded-md text-xs text-[#f4f0ec] break-all">
              {data.landing_url}
            </code>
            <button onClick={() => copy(data.landing_url)} className="btn-ghost text-xs" data-testid="dashboard-copy-link">
              <Copy size={13} /> Copy
            </button>
          </div>
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">COPY FOR STREAM OVERLAYS</div>
          <div className="flex items-start gap-2">
            <code className="flex-1 bg-[#1a1515] border border-[#2a2525] px-3 py-2.5 rounded-md text-xs text-[#a39797] leading-relaxed">
              {data.copy_blurb}
            </code>
            <button onClick={() => copy(data.copy_blurb)} className="btn-ghost text-xs" data-testid="dashboard-copy-blurb">
              <Copy size={13} />
            </button>
          </div>
        </div>

        <div className="mt-8 text-[10px] text-[#5a5050] leading-relaxed">
          Stats refresh on page load. We never show buyer emails or names. For payout questions, email <span className="text-[#a39797]">payouts@wasntme.party</span>.
        </div>
      </div>
    </div>
  );
};

export default StreamerDashboard;
